import { type DicomJson, type InstanceRef, type SeriesRef, tagString } from './dicomweb';
import type { LoaderContext } from './loader';
import { csCore } from './tools/cstypes';
import { timedFetch } from './net';
import { framePlane } from './framegeometry';
import { calibrationFrom, regionsFromJson } from './usregions';
import { resolveSpacing } from './spacing';

/**
 * Parity-mode multi-frame images as JPEG frames, the way IMEXHS shows them (`forceRasterMF: true`,
 * spec section 5): one WADO-URI request per frame with `contentType=image/jpeg&frameNumber=n`
 * (1-based), decoded in the browser and handed to cornerstone as an 8-bit colour image
 * (W/L 255/128, like the vendor's web image loader). Bit depth beyond 8 bits is lost, as in IMEXHS.
 */

export const JPEG_FRAME_SCHEME = 'imexjpeg';

const JPEG_CONTENT_TYPE = 'image/jpeg';
const WINDOW_WIDTH = 255;
const WINDOW_CENTER = 128;
const RGBA_BYTES = 4;
const TAG = {
  pixelSpacing: '00280030',
  imagerPixelSpacing: '00181164',
  ermf: '00181114',
  frameOfReference: '00200052',
} as const;

interface FrameRef {
  url: string;
  series: SeriesRef;
  instance: InstanceRef;
}

interface InstancePlane {
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
  frameOfReferenceUID?: string;
}

/** A cornerstone-core 2.6.1 colour image (the shape its canvas renderer reads). */
export interface JpegFrameImage {
  imageId: string;
  minPixelValue: number;
  maxPixelValue: number;
  slope: number;
  intercept: number;
  windowCenter: number;
  windowWidth: number;
  rows: number;
  columns: number;
  height: number;
  width: number;
  color: true;
  rgba: false;
  invert: false;
  sizeInBytes: number;
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
  getPixelData(): Uint8ClampedArray;
  getCanvas(): HTMLCanvasElement;
}

const frames = new Map<string, FrameRef>();
const planes = new Map<string, Promise<InstancePlane>>();
const resolvedPlanes = new Map<string, InstancePlane>();
let loaderContext: LoaderContext | null = null;

/** imageId of frame `frame` (1-based) of a multi-frame instance, registering the loader on first use. */
export function jpegFrameImageId(ctx: LoaderContext, series: SeriesRef, instance: InstanceRef, frame: number): string {
  ensureLoader(ctx);
  const params = new URLSearchParams({
    requestType: 'WADO',
    studyUID: series.studyInstanceUID,
    seriesUID: series.seriesInstanceUID,
    objectUID: instance.sopInstanceUID,
    contentType: JPEG_CONTENT_TYPE,
    frameNumber: String(frame),
  });
  const url = `${new URL(ctx.wadoUriRoot, window.location.href).toString()}?${params.toString()}`;
  const imageId = `${JPEG_FRAME_SCHEME}:${url}`;
  frames.set(imageId, { url, series, instance });
  return imageId;
}

/**
 * imageId of a server-rendered JPEG of frame `frame` (1-based) over WADO-RS (`…/frames/n/rendered`):
 * the "Resolución de referencia" fallback when the stored transfer syntax cannot be decoded here.
 */
export function renderedFrameImageId(ctx: LoaderContext, series: SeriesRef, instance: InstanceRef, frame: number): string {
  ensureLoader(ctx);
  const url = new URL(`${ctx.client.instanceUrl(series, instance)}/frames/${frame}/rendered`, window.location.href).toString();
  const imageId = `${JPEG_FRAME_SCHEME}:${url}`;
  frames.set(imageId, { url, series, instance });
  return imageId;
}

function ensureLoader(ctx: LoaderContext): void {
  if (loaderContext) return;
  loaderContext = ctx;
  csCore().registerImageLoader(JPEG_FRAME_SCHEME, loadJpegFrame);
  csCore().metaData.addProvider(provideMetadata);
}

function loadJpegFrame(imageId: string): { promise: Promise<JpegFrameImage>; cancelFn: () => void } {
  const controller = new AbortController();
  return { promise: fetchFrame(imageId, controller.signal), cancelFn: () => controller.abort() };
}

async function fetchFrame(imageId: string, signal: AbortSignal): Promise<JpegFrameImage> {
  const ref = frames.get(imageId);
  if (!ref || !loaderContext) throw new Error(`Unknown JPEG frame ${imageId}`);
  const headers = { Accept: JPEG_CONTENT_TYPE, ...loaderContext.client.requestHeaders() };
  const [response, plane] = await Promise.all([
    timedFetch(ref.url, { headers, credentials: 'same-origin', signal }),
    instancePlane(ref),
  ]);
  if (!response.ok) throw new Error(`WADO ${response.status} for frame ${imageId.slice(-40)}`);
  const bitmap = await createImageBitmap(await response.blob());
  try {
    return toImage(imageId, bitmap, plane);
  } finally {
    bitmap.close();
  }
}

function toImage(imageId: string, bitmap: ImageBitmap, plane: InstancePlane): JpegFrameImage {
  const { width, height } = bitmap;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('2D canvas unavailable');
  context.drawImage(bitmap, 0, 0);
  let pixels: Uint8ClampedArray | undefined;
  return {
    imageId,
    minPixelValue: 0,
    maxPixelValue: 255,
    slope: 1,
    intercept: 0,
    windowCenter: WINDOW_CENTER,
    windowWidth: WINDOW_WIDTH,
    rows: height,
    columns: width,
    height,
    width,
    color: true,
    rgba: false,
    invert: false,
    sizeInBytes: width * height * RGBA_BYTES,
    rowPixelSpacing: plane.rowPixelSpacing,
    columnPixelSpacing: plane.columnPixelSpacing,
    // RGBA, read from the canvas only when W/L, probe or ROI statistics need raw pixels.
    getPixelData: () => (pixels ??= context.getImageData(0, 0, width, height).data),
    getCanvas: () => canvas,
  };
}

/**
 * Pixel spacing and frame of reference of an instance (a JPEG carries no DICOM header). Fast mode
 * already has the metadata; parity asks for it once per multi-frame instance, as IMEXHS does with
 * its own instance-metadata call. Without it measurements fall back to pixels.
 */
function instancePlane(ref: FrameRef): Promise<InstancePlane> {
  const key = ref.instance.sopInstanceUID;
  let pending = planes.get(key);
  if (!pending) {
    pending = readInstanceMetadata(ref)
      .then((json) => planeFrom(json))
      .catch((error: unknown) => {
        console.warn('[viewer] instance metadata unavailable; measurements in pixels', error);
        return {};
      })
      .then((plane) => {
        resolvedPlanes.set(key, plane);
        return plane;
      });
    planes.set(key, pending);
  }
  return pending;
}

async function readInstanceMetadata(ref: FrameRef): Promise<DicomJson | undefined> {
  if (ref.instance.metadata) return ref.instance.metadata;
  if (!loaderContext) return undefined;
  const { client } = loaderContext;
  const response = await timedFetch(`${client.instanceUrl(ref.series, ref.instance)}/metadata`, {
    headers: { Accept: 'application/dicom+json', ...client.requestHeaders() },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`metadata ${response.status}`);
  const rows = (await response.json()) as DicomJson[];
  return rows[0];
}

function planeFrom(json: DicomJson | undefined): InstancePlane {
  const resolved = resolveSpacing({
    pixelSpacing: json?.[TAG.pixelSpacing]?.Value,
    imagerPixelSpacing: json?.[TAG.imagerPixelSpacing]?.Value,
    ermf: json?.[TAG.ermf]?.Value,
  });
  // Enhanced multi-frame keeps Pixel Spacing in its functional groups, not at the top level.
  const enhanced = resolved ? undefined : framePlane(json, 1);
  const row = resolved?.row ?? enhanced?.rowPixelSpacing ?? NaN;
  const column = resolved?.column ?? enhanced?.columnPixelSpacing ?? NaN;
  const calibrated = Number.isFinite(row) && row > 0 && Number.isFinite(column) && column > 0;
  // Ultrasound cine clips rarely have Pixel Spacing: their calibrated 2D region gives it (usregions.ts).
  const us = calibrated ? undefined : calibrationFrom(regionsFromJson(json));
  return {
    rowPixelSpacing: calibrated ? row : us?.rowPixelSpacing,
    columnPixelSpacing: calibrated ? column : us?.columnPixelSpacing,
    frameOfReferenceUID: tagString(json, TAG.frameOfReference) || undefined,
  };
}

/** Metadata of our JPEG frame imageIds for the measurement tools and overlays. */
function provideMetadata(type: string, imageId: string): unknown {
  const ref = frames.get(imageId);
  if (!ref) return undefined;
  if (type === 'generalSeriesModule') {
    return { modality: ref.series.modality, seriesInstanceUID: ref.series.seriesInstanceUID, studyInstanceUID: ref.series.studyInstanceUID };
  }
  if (type === 'imagePlaneModule') {
    const plane = resolvedPlanes.get(ref.instance.sopInstanceUID);
    return plane ? { rowPixelSpacing: plane.rowPixelSpacing, columnPixelSpacing: plane.columnPixelSpacing, frameOfReferenceUID: plane.frameOfReferenceUID } : undefined;
  }
  return undefined;
}
